/**
 * Pending Assistant Turn
 *
 * Shared helpers for assistant messages that represent an in-flight response.
 *
 * Primary responsibilities:
 * - Mark assistant messages as pending or completed
 * - Detect active pending assistant turns from persisted chat messages
 *
 * Dependencies:
 * - Supabase JSON types
 */

import type { Json, Tables } from '@/supabase/types'
import type { ChatMessage } from '@/types'

const PENDING_ASSISTANT_TURN_TTL_MS = 15 * 60 * 1000

type MessageLike = Pick<
  Tables<'messages'>,
  'content' | 'metadata' | 'role' | 'created_at'
>

export interface PendingAssistantTurnState {
  message: ChatMessage
  startedAt: number
  firstTokenReceived: boolean
}

// --------------

function getMetadataRecord(metadata: Json | null | undefined) {
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? (metadata as Record<string, Json>)
    : null
}

function getNumberMetadataValue(
  metadata: Record<string, Json> | null,
  key: string
) {
  const value = metadata?.[key]

  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function hasTerminalStreamMarker(metadata: Record<string, Json> | null) {
  return Boolean(
    getNumberMetadataValue(metadata, 'streamCompletedAt') ||
      getNumberMetadataValue(metadata, 'streamFailedAt') ||
      getNumberMetadataValue(metadata, 'streamStoppedAt')
  )
}

// --------------

/**
 * Shared terminal-state reader used by both the pending-turn detector and the
 * realtime message reconciler, so "is this turn done?" has ONE definition.
 * A turn is terminal when its streamStatus is completed/failed/stopped OR a
 * terminal timestamp marker is present. `terminalAt` is the latest terminal
 * timestamp (0 when non-terminal), used as a monotonic version component.
 */
export function getAssistantTurnTerminalState(
  metadata: Json | null | undefined
): { isTerminal: boolean; terminalAt: number } {
  const record = getMetadataRecord(metadata)
  const streamStatus = record?.streamStatus
  const terminalAt = Math.max(
    getNumberMetadataValue(record, 'streamCompletedAt') ?? 0,
    getNumberMetadataValue(record, 'streamFailedAt') ?? 0,
    getNumberMetadataValue(record, 'streamStoppedAt') ?? 0
  )
  const isTerminal =
    streamStatus === 'completed' ||
    streamStatus === 'failed' ||
    streamStatus === 'stopped' ||
    hasTerminalStreamMarker(record)

  return { isTerminal, terminalAt }
}

function buildChatContextCompactionStatusUpdate(
  metadata: Record<string, Json>,
  status: 'completed' | 'failed' | 'stopped'
): Record<string, Json> {
  const chatContextCompaction = getMetadataRecord(metadata.chatContextCompaction)

  if (!chatContextCompaction) {
    return {}
  }

  return {
    chatContextCompaction: {
      ...chatContextCompaction,
      status
    }
  }
}

// --------------

export function buildPendingAssistantTurnMetadata(
  startedAt: number,
  metadata: Json | null = null
): Json {
  const existing = getMetadataRecord(metadata)

  return {
    ...(existing ?? {}),
    streamStartedAt: startedAt,
    streamStatus: 'running'
  } as Json
}

export function completePendingAssistantTurnMetadata(
  metadata: Json | null | undefined,
  completedAt = Date.now()
): Json | null {
  const existing = getMetadataRecord(metadata)

  if (!existing?.streamStartedAt) {
    return metadata ?? null
  }

  return {
    ...existing,
    ...buildChatContextCompactionStatusUpdate(existing, 'completed'),
    streamCompletedAt: completedAt,
    streamStatus: 'completed'
  } as Json
}

export function failPendingAssistantTurnMetadata(
  metadata: Json | null | undefined,
  failedAt = Date.now()
): Json | null {
  const existing = getMetadataRecord(metadata)

  if (!existing?.streamStartedAt) {
    return metadata ?? null
  }

  return {
    ...existing,
    ...buildChatContextCompactionStatusUpdate(existing, 'failed'),
    streamFailedAt: failedAt,
    streamStatus: 'failed'
  } as Json
}

export function stopPendingAssistantTurnMetadata(
  metadata: Json | null | undefined,
  stoppedAt = Date.now()
): Json | null {
  const existing = getMetadataRecord(metadata)

  if (!existing?.streamStartedAt) {
    return metadata ?? null
  }

  return {
    ...existing,
    ...buildChatContextCompactionStatusUpdate(existing, 'stopped'),
    streamStoppedAt: stoppedAt,
    streamStatus: 'stopped'
  } as Json
}

export function getPendingAssistantTurnStartedAt(message: MessageLike) {
  if (message.role !== 'assistant') {
    return null
  }

  const metadata = getMetadataRecord(message.metadata)
  const startedAt = getNumberMetadataValue(metadata, 'streamStartedAt')
  const streamStatus = metadata?.streamStatus

  if (streamStatus !== 'running' || !startedAt || hasTerminalStreamMarker(metadata)) {
    return null
  }

  const isFresh = Date.now() - startedAt < PENDING_ASSISTANT_TURN_TTL_MS
  return isFresh ? startedAt : null
}

export function getLatestPendingAssistantTurnState(
  messages: ChatMessage[]
): PendingAssistantTurnState | null {
  const sortedMessages = [...messages].sort(
    (left, right) => left.message.sequence_number - right.message.sequence_number
  )

  for (let index = sortedMessages.length - 1; index >= 0; index -= 1) {
    const message = sortedMessages[index]
    const startedAt = getPendingAssistantTurnStartedAt(message.message)

    if (!startedAt) {
      continue
    }

    // Anything after this "pending" assistant means the thread already moved
    // on (force-send, queued follow-up, or a second user turn). Keeping the
    // loader / send-lock on that stale turn is what makes Send queue forever
    // and leaves a spinner above later user messages.
    if (index < sortedMessages.length - 1) {
      continue
    }

    const content =
      typeof message.message.content === 'string' ? message.message.content : ''

    return {
      message,
      startedAt,
      firstTokenReceived: content.trim().length > 0
    }
  }

  return null
}

/**
 * Mark every pending assistant that already has a later message as stopped.
 * Used when force-sending / syncing so a finished-looking turn cannot keep
 * the composer locked.
 */
export function stopStalePendingAssistantTurns(
  messages: ChatMessage[]
): ChatMessage[] {
  const sortedMessages = [...messages].sort(
    (left, right) => left.message.sequence_number - right.message.sequence_number
  )

  let changed = false
  const next = sortedMessages.map((chatMessage, index) => {
    const startedAt = getPendingAssistantTurnStartedAt(chatMessage.message)
    if (!startedAt || index >= sortedMessages.length - 1) {
      return chatMessage
    }

    changed = true
    return {
      ...chatMessage,
      message: {
        ...chatMessage.message,
        metadata: stopPendingAssistantTurnMetadata(
          chatMessage.message.metadata
        ) as ChatMessage['message']['metadata']
      }
    }
  })

  return changed ? next : messages
}
